/**
 * ParticipantWorkspacePage - Dedicated workspace for ROLE_MEMBER participants
 *
 * Single-screen, focused experience for task execution without tabs or plan browsing.
 * Redirects to here from PlanDetailPage when user is ROLE_MEMBER.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  CircularProgress,
  Alert,
  Typography,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockIcon from '@mui/icons-material/Lock';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useAuth } from '../contexts/AuthContext';
import { colors } from '../theme/colors';
import {
  GET_PARTICIPANT_WORKSPACE,
  UPDATE_STEP_CONTEXT,
  TOGGLE_STEP_COMPLETION,
} from '../graphql/queries';
import {
  ParticipantHeader,
  MyTasksList,
  FuzzyContextPanel,
  ParticipantStepDetail,
} from '../components/participant-workspace';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

function ParticipantWorkspacePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { user, loading: authLoading } = useAuth();
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);

  // Redirect to auth if not logged in
  useEffect(() => {
    if (!authLoading && !user) {
      sessionStorage.setItem('redirectAfterLogin', location.pathname);
      navigate('/auth');
    }
  }, [user, authLoading, navigate, location.pathname]);

  // Query participant workspace
  const {
    loading,
    error,
    data,
    refetch,
  } = useQuery(GET_PARTICIPANT_WORKSPACE, {
    variables: { planId: id || '' },
    skip: !id || !user,
  });
  useDocumentTitle('Plans', data?.participantWorkspace?.planTitle);

  // Mutations
  const [updateStepContext, { loading: updatingContext }] = useMutation(UPDATE_STEP_CONTEXT, {
    onCompleted: () => refetch(),
  });

  const [toggleStepCompletion, { loading: togglingCompletion }] = useMutation(TOGGLE_STEP_COMPLETION, {
    onCompleted: () => refetch(),
  });

  // Select first step by default when data loads
  useEffect(() => {
    if (data?.participantWorkspace?.mySteps?.length > 0 && !selectedStepId) {
      // Select first non-completed step, or first step if all completed
      const firstActive = data.participantWorkspace.mySteps.find(
        (s: any) => s.status !== 'COMPLETED'
      );
      setSelectedStepId(firstActive?.stepId || data.participantWorkspace.mySteps[0].stepId);
    }
  }, [data, selectedStepId]);

  const handleStepSelect = useCallback((stepId: string) => {
    setSelectedStepId(stepId);
  }, []);

  const handleToggleCompletion = useCallback(
    async (stepId: string, completed: boolean) => {
      if (!id) return;
      await toggleStepCompletion({
        variables: {
          planId: id,
          stepId,
          completed,
        },
      });
    },
    [id, toggleStepCompletion]
  );

  const handleUpdateContext = useCallback(
    async (stepId: string, context: { notes?: string; links?: string[] }) => {
      if (!id) return;
      await updateStepContext({
        variables: {
          planId: id,
          stepId,
          context: {
            notes: context.notes || null,
            links: context.links || [],
          },
        },
      });
    },
    [id, updateStepContext]
  );

  // Loading state
  if (authLoading || !user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    // Handle specific error cases
    if (
      error.message.includes('Use projectedPlan') ||
      error.message.includes('BAD_REQUEST')
    ) {
      // User is owner or ADMIN - redirect to full plan view
      navigate(`/app/plans/${id}`, { replace: true });
      return null;
    }

    if (
      error.message.includes('Access denied') ||
      error.message.includes('FORBIDDEN')
    ) {
      return (
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              bgcolor: 'background.paper',
              borderRadius: 2,
            }}
          >
            <LockIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Access Denied
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              You don't have permission to view this plan.
            </Typography>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/app/plans')}
              variant="contained"
            >
              Back to Plans
            </Button>
          </Box>
        </Container>
      );
    }

    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Error loading workspace: {error.message}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/app/plans')}
          sx={{ mt: 2 }}
        >
          Back to Plans
        </Button>
      </Container>
    );
  }

  const workspace = data?.participantWorkspace;

  if (!workspace) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">Workspace not found</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/app/plans')}
          sx={{ mt: 2 }}
        >
          Back to Plans
        </Button>
      </Container>
    );
  }

  const selectedStep = workspace.mySteps.find(
    (s: any) => s.stepId === selectedStepId
  );

  return (
    <Box sx={{ flex: 1, bgcolor: colors.background, minHeight: '100vh' }}>
      {/* Header */}
      <ParticipantHeader
        planId={workspace.planId}
        planTitle={workspace.planTitle}
        planStatus={workspace.planStatus}
        updatedAt={workspace.updatedAt}
        totals={workspace.fuzzyContext.totals}
        visibleSteps={workspace.fuzzyContext.visibleSteps}
        totalSteps={workspace.fuzzyContext.totalSteps}
      />

      {/* Main content */}
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3,
          }}
        >
          {/* Left column: Task list */}
          <Box sx={{ width: { xs: '100%', md: '33%' }, flexShrink: 0 }}>
            <MyTasksList
              steps={workspace.mySteps}
              selectedStepId={selectedStepId}
              onStepSelect={handleStepSelect}
            />
          </Box>

          {/* Center column: Step detail */}
          <Box sx={{ flex: 1 }}>
            {selectedStep ? (
              <ParticipantStepDetail
                step={selectedStep}
                onToggleCompletion={handleToggleCompletion}
                onUpdateContext={handleUpdateContext}
                isUpdating={updatingContext || togglingCompletion}
              />
            ) : (
              <Box
                sx={{
                  p: 4,
                  textAlign: 'center',
                  border: `1px solid ${colors.border}`,
                  borderRadius: 2,
                  bgcolor: colors.card,
                }}
              >
                <Typography color="text.secondary">
                  Select a task to view details
                </Typography>
              </Box>
            )}
          </Box>

          {/* Right column: Progress context */}
          <Box sx={{ width: { xs: '100%', md: '25%' }, flexShrink: 0 }}>
            <FuzzyContextPanel fuzzyContext={workspace.fuzzyContext} />
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default ParticipantWorkspacePage;
